// src/features/pdf/pdf-reorder/PdfReorder.tsx
import React from 'react';
import { Box, Container, Typography, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Accept } from 'react-dropzone';
import { usePdfReorder } from './hooks/usePdfReorder';
import PDFToolWorkspace from '../components/PDFToolWorkspace';
import DraggablePageList from './components/DraggablePageList';
import ToolActionArea from '../components/ToolActionArea';
import PdfViewerModal from '../components/PdfViewerModal';
import StyledFAQ from '../components/StyledFAQ';

const PdfReorder: React.FC = () => {
    const { t } = useTranslation();
    const {
        files,
        pages,
        status,
        outputFile,
        error,
        viewerFile,
        handleDrop,
        handleDeleteFile,
        handleReorderFiles,
        handleReorderPages,
        handleDeletePage,
        handlePreviewPage,
        handleCloseViewer,
        handleProcess,
        handleReset,
        handleDownload,
        handlePreviewOutput,
    } = usePdfReorder();

    const faqs = [
        { q: 'pdf_reorder_faq1_q', a: 'pdf_reorder_faq1_a' },
        { q: 'pdf_reorder_faq2_q', a: 'pdf_reorder_faq2_a' },
        { q: 'pdf_reorder_faq3_q', a: 'pdf_reorder_faq3_a' },
    ];

    const acceptedFiles: Accept = { 'application/pdf': ['.pdf'] };
    const hasActivePages = pages.some(p => !p.deleted);

    return (
        <>
            <PdfViewerModal
                open={viewerFile !== null}
                onClose={handleCloseViewer}
                file={viewerFile}
            />

            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Stack spacing={4} alignItems="center">
                    <PDFToolWorkspace
                        titleKey="pdf_reorder_page"
                        descKey="pdf_reorder_page_desc"
                        acceptedFiles={acceptedFiles}
                        allowMultiple={false}
                        allowReordering={false} // File reordering is not applicable
                        disabled={status === 'loading'}
                        files={files}
                        onDrop={handleDrop}
                        onDelete={handleDeleteFile}
                        onReorder={handleReorderFiles}
                        // onFileClick can be used to open a preview of the source file if needed
                    >
                        {/* The DraggablePageList now acts as the tool's "options" area */}
                        {pages.length > 0 && (
                            <Box sx={{ width: '100%', mt: 2 }}>
                                <Typography variant="h5" component="h3" align="center" gutterBottom>
                                    {t('pdf_reorder_page_list_title', { ns: 'tools' })}
                                </Typography>
                                <DraggablePageList
                                    pages={pages}
                                    onReorder={handleReorderPages}
                                    onDelete={handleDeletePage}
                                    onPreview={handlePreviewPage}
                                />
                            </Box>
                        )}
                    </PDFToolWorkspace>

                    {/* Action area is now outside, mirroring PdfSplitter */}
                    <ToolActionArea
                        status={status}
                        onAction={handleProcess}
                        onReset={handleReset}
                        onDownloadAll={outputFile ? handleDownload : undefined}
                        onPreviewOutput={outputFile ? handlePreviewOutput : undefined}
                        actionButtonText={t('pdf_reorder_action_button', { ns: 'tools' })}
                        successTitle={t('pdf_reorder_success_title', { ns: 'tools' })}
                        successMessage={t('pdf_reorder_success_desc', { ns: 'tools' })}
                        errorMessage={error}
                        outputFiles={outputFile ? [outputFile] : []}
                        disabled={files.length === 0 || !hasActivePages || status === 'loading'}
                    />
                </Stack>

                <Box sx={{ my: 15, maxWidth: '900px', mx: 'auto' }}>
                    <Typography sx={{ mb: 2 }} variant="h4" component="h2" gutterBottom textAlign="center">
                        {t('how_to_reorder_pdf_title', { ns: 'tools' })}
                    </Typography>
                    <Typography sx={{ mb: 6 }} paragraph color="text.secondary" textAlign="center">
                        {t('how_to_reorder_pdf_desc', { ns: 'tools' })}
                    </Typography>

                    <Typography sx={{ mb: 2, mt: 6 }} variant="h5" component="h3" gutterBottom textAlign="center">
                        {t('why_reorder_pdf_title', { ns: 'tools' })}
                    </Typography>
                    <Typography sx={{ mb: 6 }} paragraph color="text.secondary" textAlign="center">
                        {t('why_reorder_pdf_p1', { ns: 'tools' })}
                    </Typography>

                    <StyledFAQ
                        title={t('faq_title', { ns: 'tools' })}
                        faqs={faqs}
                        t={t}
                    />
                </Box>
            </Container>
        </>
    );
};

export default PdfReorder;